import { LitElement, html } from 'lit-element';

import connect from '@elab/core/store/connect';
import { socialNames } from '@elab/core/utils/constants';

import { actions as authActions, selectors as authSelectors } from '../../store/auth';
import { selectors as configSelectors } from '../../store/config';
import { selectors as globalSelectors } from '../../store/global';
import { jsLazyLoad } from '@elab/core/utils/webComponent';
import { getMobilePlatformType } from '@elab/core/utils/device';

export class SocialLogin extends LitElement {
    static get properties() {
        return {
            socialArray: { type: Array, attribute: 'social-array' },
            facebookPermissions: {
                type: String,
                attribute: 'facebook-permissions',
            },
            autologinRedirectLink: { type: String, attribute: 'autologin-redirect-link' },
            successRedirectLink: { type: String, attribute: 'success-redirect-link' },
            failRedirectLink: { type: String, attribute: 'fail-redirect-link' },
            appId: { type: String, attribute: 'app-id' },
            isWtr: { type: Boolean, attribute: 'is-wtr' },
            isRegPage: { type: Boolean, attribute: 'is-reg-page' },
            facebookActionText: { type: String, attribute: 'facebook-action-text' },
            facebookFeatureFlag: { type: Boolean, attribute: 'facebook-feature-flag' },
            googleFeatureFlag: { type: Boolean, attribute: 'google-feature-flag' },
            googleClientId: { type: String, attribute: 'google-client-id' },
            googleSdkSrc: { type: String, attribute: 'google-sdk-src' },
            googleActionText: { type: String, attribute: 'google-action-text' },
        };
    }

    constructor() {
        super();

        this.socialArray = [];
        this.facebookPermissions = '';
        this.autologinRedirectLink = '';
        this.failRedirectLink = '';
        this.appId = '';
        this.isWtr = false;
        this.facebookActionText = '';
        this.googleUser = {};
        this.facebookFeatureFlag = false;
        this.googleFeatureFlag = false;
        this.googleClientId = '';
        this.googleSdkSrc = '';
        this.googleActionText = '';
        this.isRegPage = false;
    }

    async firstUpdated() {
        if (this.isWtr) {
            this.dispatchEvent(new CustomEvent('set-social-authId', { detail: this.appId }));
        }

        try {
            if (this.googleFeatureFlag) {
                window.document.addEventListener('google-at', this.handleGoogleAt, false);
                if (window.google && window.google.accounts) {
                    this.googleLoginInit();
                } else {
                    console.error('Google SDK not loaded');
                }
            }
        } catch (error) {
            console.log('Google SDK error ', error);
        }
    }

    googleLoginInit() {
        window.handleSignInWithGoogle = async function(response) {
            window.document.dispatchEvent(
                new CustomEvent('google-at', {
                    detail: {
                        accessToken: response.credential,
                        isRegistrationPage: this.isRegPage ? true : false,
                    },
                }),
            );
        };

        window.google.accounts.id.initialize({
            client_id: this.googleClientId,
            callback: window.handleSignInWithGoogle,
            scope: 'email',
            context: 'signin',
            ux_mode: 'popup',
            nonce: '',
            auto_select: true,
            itp_support: true,
            prompt: 'consent',
        });

        const googleBtnNode = document.querySelectorAll('.social-login--google');
        for (let node = 0; node < googleBtnNode.length; node++) {
            if (!googleBtnNode[node].hasChildNodes()) {
                window.google.accounts.id.renderButton(googleBtnNode[node], {
                    type: 'standard',
                    shape:
                        (getMobilePlatformType() === 'android' ||
                            getMobilePlatformType() === 'ios') &&
                        this.isRegPage
                            ? 'pill'
                            : 'rectangle',
                    theme: 'outline',
                    locale: 'tr',
                    text: 'continue_with',
                    size: 'large',
                    logo_alignment: 'center',
                    callback: '${callback}',
                });
                break;
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    handleFacebookCtaClick(e) {
        window.document.addEventListener('facebook-at', this.handleFacebookAt, false);
        checkLoginState(e);
    }

    handleFacebookAt = e => {
        const { accessToken } = e.detail;
        const facebookLoginDisableSelector = 'social-login--disable';
        const fbButtonSelector = '.fb-lgn-btn';
        const googleButtonSelector = '.social-login--google';
        const googleLoginDisableSelector = 'google-login--disable';
        if (accessToken) {
            if (this.shouldRedirectToPDPAfterLogin && this.prevPageData && this.prevPageData.url) {
                this.autologinRedirectLink = this.prevPageData.url;
            }
            this.dispatchEvent(
                new CustomEvent('social-registration', {
                    detail: {
                        socialName: socialNames.FACEBOOK,
                        accessToken,
                        autologinRedirectLink: this.autologinRedirectLink,
                        successRedirectLink: this.successRedirectLink,
                        failRedirectLink: this.failRedirectLink,
                        isRegPage: this.isRegPage,
                    },
                }),
            );
            if (this.isWtr) {
                const fbLgnBtnNode = document.querySelectorAll(fbButtonSelector);
                const googleLgnBtnNode = document.querySelectorAll(googleButtonSelector);
                fbLgnBtnNode.forEach(element => {
                    element.classList.add(facebookLoginDisableSelector);
                });
                googleLgnBtnNode.forEach(element => {
                    element.classList.add(googleLoginDisableSelector);
                });
            }
            sessionStorage.removeItem('googleAuthToken');
        }
    };

    handleGoogleAt = e => {
        const { accessToken, isRegistrationPage } = e.detail;
        const fbButtonSelector = '.fb-lgn-btn';
        const facebookLoginDisableSelector = 'social-login--disable';
        const googleButtonSelector = '.social-login--google';
        const googleLoginDisableSelector = 'google-login--disable';
        if (accessToken) {
            sessionStorage.setItem('googleAuthToken', accessToken);
            if (this.shouldRedirectToPDPAfterLogin && this.prevPageData && this.prevPageData.url) {
                this.autologinRedirectLink = this.prevPageData.url;
            }
            this.dispatchEvent(
                new CustomEvent('social-registration', {
                    detail: {
                        socialName: socialNames.GOOGLE,
                        accessToken,
                        autologinRedirectLink: this.autologinRedirectLink,
                        successRedirectLink: this.successRedirectLink,
                        failRedirectLink: this.failRedirectLink,
                        isRegPage: isRegistrationPage,
                    },
                }),
            );
            if (this.isWtr) {
                const fbLgnBtnNode = document.querySelectorAll(fbButtonSelector);
                const googleLgnBtnNode = document.querySelectorAll(googleButtonSelector);
                googleLgnBtnNode.forEach(element => {
                    element.classList.add(googleLoginDisableSelector);
                });
                fbLgnBtnNode.forEach(element => {
                    element.classList.add(facebookLoginDisableSelector);
                });
            }
            sessionStorage.removeItem(`fbssls_${this.appId}`);
        }
    };

    renderFacebook(social) {
        const facebookToken = sessionStorage.getItem(`fbssls_${this.appId}`);
        const googleToken = sessionStorage.getItem('googleAuthToken');
        return html`
            <div class="social-login--${social}">
                ${!this.isWtr
                    ? html`
                          <div
                              class="fb-login-button"
                              style="display: block; margin: 20px 0 0;"
                              data-scope="${this.facebookPermissions}"
                              data-onlogin="checkFBLoginState();"
                              data-size="large"
                              data-button-type="login_with"
                              data-layout="default"
                              data-auto-logout-link="false"
                              data-use-continue-as="false"
                              data-width="100%"
                          ></div>
                      `
                    : html`
                          <div
                              type="button"
                              class="fb-lgn-btn ${googleToken || facebookToken
                                  ? 'social-login--disable'
                                  : ''}"
                              @click="${this.handleFacebookCtaClick}"
                          >
                              <span class="facebook-icon"><i class="icon icon-facebook"></i></span>
                              <span class="buttonText">${this.facebookActionText}</span>
                          </div>
                      `}
            </div>
        `;
    }

    renderGoogle(social) {
        const facebookToken = sessionStorage.getItem(`fbssls_${this.appId}`);
        const googleToken = sessionStorage.getItem('googleAuthToken');
        return html`
            ${this.isWtr
                ? html`
                      <div
                          type="button"
                          class="social-login--google ${googleToken || facebookToken
                              ? 'google-login--disable'
                              : ''}"
                          id="googlesdkbtn"
                      ></div>
                  `
                : ''}
        `;
    }

    renderSocialButton = social => {
        switch (social) {
            case socialNames.FACEBOOK:
                return this.facebookFeatureFlag ? this.renderFacebook(social) : null;
            case socialNames.GOOGLE:
                return this.googleFeatureFlag ? this.renderGoogle(social) : null;
            default:
        }
    };

    render() {
        return html`
            <div class="social-login__wrapper">
                ${this.socialArray.map(this.renderSocialButton)}
            </div>
        `;
    }

    createRenderRoot() {
        return this;
    }
}

const mapStateToProps = state => ({
    prevPageData: globalSelectors.getPrevPageData(state),
    shouldRedirectToPDPAfterLogin: configSelectors.shouldRedirectToPDPAfterLogin(state),
});

export const mapDispatchToEvents = dispatch => ({
    'social-registration': ({ detail }) => dispatch(authActions.socialRegistration(detail)),
    'set-social-authId': ({ detail }) => dispatch(authActions.setFbAppId(detail)),
});

export default () =>
    customElements.define(
        'e2-social-login',
        connect(mapStateToProps, mapDispatchToEvents)(SocialLogin),
    );
